import { useEffect, useRef } from 'react';
import { FormikErrors, FormikProps, setNestedObjectValues } from 'formik';
import { isEmpty, isEqual } from 'lodash';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import { ParsedCard, ParsedForm, ParsedHouseConsignment } from 'store/declaration-form-errors/ParsedForm';

interface Props<TDeclaration> {
    validationErrorsParser: ((errors: FormikErrors<TDeclaration>) => ParsedForm) | undefined;
    formik: FormikProps<any> | undefined;
    productsFormik: FormikProps<any> | undefined;
    houseConsignmentFormik?: FormikProps<any> | undefined;
    houseConsignmentErrorsParser?: (errors: any) => ParsedCard[];
    houseConsignmentIndex?: number;
    triedToSubmit: boolean;
}

const useHandleErrors = <TDeclaration,>({
    validationErrorsParser,
    formik,
    productsFormik,
    houseConsignmentFormik,
    houseConsignmentErrorsParser,
    houseConsignmentIndex,
    triedToSubmit,
}: Props<TDeclaration>) => {
    const { declarationErrors, hasFormDeclarationErrors, setFormDeclarationErrors } = useDeclarationFormErrors();

    /**
     * The values the errors the form holds were validated against. Saving or refetching the
     * declaration reinitializes the form, and reinitializing resets its errors - so between that
     * reset and the revalidation below the form reports no errors without anything having been
     * validated, which is not the same as a form that passes.
     */
    const validatedValues = useRef(formik?.initialValues);

    useEffect(() => {
        if (validationErrorsParser === undefined) return;

        if (!triedToSubmit && !hasFormDeclarationErrors) return;

        // Reporting the errors of a form that has just been reinitialized would clear everything the
        // last validation found - of every item and house consignment, not only of what changed.
        if (formik && formik.initialValues !== validatedValues.current) return;

        if (formik?.errors) setFormDeclarationErrors(validationErrorsParser(formik.errors));

        // The house consignment form has an effect of its own below - the master form it would be
        // parsed from here does not hold its values until the declaration is saved.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik?.errors, productsFormik?.errors]);

    /**
     * The house consignment being edited is reported by the master form, which is only handed its
     * values when the declaration is saved - until then the store holds what the last submission
     * attempt found. So the entry of the consignment that is open is kept up to date from its own
     * form instead, which is what clears the marks on its cards, on the house consignments list and
     * on the tab as the user fills the fields in. Its goods items are left alone: they are saved
     * through their own endpoint, and are re-reported when they are.
     */
    useEffect(() => {
        if (!(houseConsignmentErrorsParser && houseConsignmentFormik && houseConsignmentIndex)) return;

        if (!triedToSubmit && !hasFormDeclarationErrors) return;

        // The form validates itself on mount, and its errors are empty until it does - reporting
        // that as the state of the consignment would clear its marks for as long as it takes.
        if (!houseConsignmentFormik.dirty && isEmpty(houseConsignmentFormik.touched)) return;

        const houseConsignments = declarationErrors.houseConsignments ?? [];
        const current = houseConsignments.find(({ index }) => index === houseConsignmentIndex);
        const errors = houseConsignmentErrorsParser(houseConsignmentFormik.errors);

        if (isEqual(current?.errors ?? [], errors)) return;

        const updated: ParsedHouseConsignment = { ...current, index: houseConsignmentIndex, errors };
        let next: ParsedHouseConsignment[];

        if (!errors.length && !updated.items?.length) {
            next = houseConsignments.filter(({ index }) => index !== houseConsignmentIndex);
        } else if (current) {
            next = houseConsignments.map((entry) => (entry.index === houseConsignmentIndex ? updated : entry));
        } else {
            next = [...houseConsignments, updated].sort((a, b) => a.index - b.index);
        }

        setFormDeclarationErrors({ ...declarationErrors, houseConsignments: next });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [houseConsignmentFormik?.errors, houseConsignmentIndex]);

    useEffect(() => {
        // Whatever comes out of the validation below is what the new values have been validated
        // against, so the errors it reports are reported rather than skipped.
        validatedValues.current = formik?.initialValues;

        if (hasFormDeclarationErrors) {
            formik?.validateForm().then((validations) => {
                formik?.setTouched(setNestedObjectValues(validations, true));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik?.initialValues]);

    useEffect(() => {
        if (hasFormDeclarationErrors) {
            productsFormik?.validateForm().then((validations) => {
                productsFormik?.setTouched(setNestedObjectValues(validations, true));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productsFormik?.initialValues]);

    useEffect(() => {
        if (hasFormDeclarationErrors) {
            houseConsignmentFormik?.validateForm().then((validations) => {
                houseConsignmentFormik?.setTouched(setNestedObjectValues(validations, true));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [houseConsignmentFormik?.initialValues]);
};

export default useHandleErrors;
